<script setup lang="ts">
/**
 * The bell's popup (W11): the newest few notifications, whatever the page's
 * filters, each a link to what it is about. Opening one marks it read.
 *
 * Titles are printed as text. The fixture era rendered them with `v-html`
 * because the mockup put `<strong>` in its strings; the server renders the
 * words now, and nothing from a payload is ever handed to `v-html`.
 */
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useUIStore } from '@/stores/ui.store'
import { useNotificationsStore } from '@/stores/notifications.store'
import { useAnchor } from '@/composables/useAnchor'
import { useToast } from '@/composables/useToast'
import { formatWhen } from '@/utils/caseText'
import type { Notification } from '@/entities/notification'

const { t } = useI18n()
const ui = useUIStore()
const router = useRouter()
const notifications = useNotificationsStore()
const { toast } = useToast()
const unreadCount = computed(() => notifications.unreadCount)

/* Re-read on every opening: the popup is small and the stream only says a
 * count changed, not what arrived. */
watch(
  () => ui.notifOpen,
  (open) => {
    if (open) void notifications.fetchRecent()
  }
)

/* Pin under the topbar bell button — found by a data attribute rather than
 * its aria-label, which is translated. */
const anchorStyle = useAnchor(
  () => ui.notifOpen,
  () => document.querySelector('.topbar__action[data-bell]')
)

async function open(n: Notification) {
  ui.closePopups()
  if (n.unread) notifications.markRead(n.id).catch(() => {})
  if (n.to) await router.push(n.to)
}

async function markAll() {
  try {
    await notifications.markAllRead()
  } catch (err) {
    toast({ title: t('notif.failed'), sub: (err as Error)?.message, type: 'error' })
  }
}
</script>

<template>
  <div
    id="tb-notif-pop"
    class="tb-pop notif-pop"
    :class="{ 'is-open': ui.notifOpen }"
    :style="anchorStyle"
    role="dialog"
    :aria-label="t('topbar.notifications')"
    @click.stop
  >
    <header class="tb-pop__head">
      <div class="tb-pop__title">
        {{ t('topbar.notifications') }}
        <span v-if="unreadCount" class="tag tag--high">
          {{ t('notif.popupNew', { n: unreadCount }) }}
        </span>
      </div>
      <button v-if="unreadCount" class="tb-pop__action" type="button" @click="markAll">
        {{ t('notif.markAllRead') }}
      </button>
    </header>

    <div class="notif-list">
      <p v-if="!notifications.recent.length" class="notif-empty">
        {{ notifications.recentLoaded ? t('notif.popupEmpty') : t('common.loading') }}
      </p>
      <a
        v-for="n in notifications.recent"
        :key="n.id"
        :href="n.to ?? '#'"
        class="notif-item"
        :class="{ 'is-unread': n.unread }"
        @click.prevent="open(n)"
      >
        <span class="notif-item__icon" :class="n.tone ? `notif-item__icon--${n.tone}` : ''">
          <i :class="n.icon" aria-hidden="true"></i>
        </span>
        <div class="notif-item__txt">
          <div class="notif-item__title">{{ n.title }}</div>
          <div class="notif-item__meta">{{ n.body }}</div>
        </div>
        <div class="notif-item__when">{{ formatWhen(n.createdAt) }}</div>
      </a>
    </div>

    <footer class="tb-pop__foot">
      <RouterLink :to="{ name: 'notifications' }" @click="ui.closePopups()">
        {{ t('notif.viewAll') }}
        <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
      </RouterLink>
    </footer>
  </div>
</template>

<style scoped>
/* The body sentence can be long ("The scan found 12 new high-risk matches on 5
 * of your brands: …"); two lines in the popup, the rest on the page. */
.notif-item__meta {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
